<script setup>
// Import functions
// Import components
import OmniSelect from 'components/UI/OmniSelect.vue'
import { cloneDeep, isEqual } from 'lodash'
import { format } from 'quasar'
import { getOptions } from 'src/utils/utils'

// Constants declarations
const $q = useQuasar()
const { capitalize } = format
useI18n({ useScope: 'global' })
const slots = useSlots()

// Component props
const props = defineProps({
  addItem: {
    type: Function,
    required: false,
    default: null,
  },
  addItemLabel: {
    type: [null, String],
    required: false,
    default: null,
  },
  refreshItemList: {
    type: Function,
    required: false,
    default: null,
  },
  filters: {
    type: Object,
    required: false,
    default: () => {
      return {}
    },
  },
  itemsService: {
    type: String,
    required: true,
    default: '',
  },
  heading: {
    type: String,
    required: false,
    default: null,
  },
  headingTag: {
    type: String,
    required: false,
    default: 'h1',
  },
  searchable: {
    type: Boolean,
    required: false,
    default: true,
  },
  removeTopPadding: {
    type: Boolean,
    required: false,
    default: false,
  },
  translateFilterOptions: {
    type: Boolean,
    required: false,
    default: true,
  },
  additionalButtons: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  largeActionButtons: {
    type: Boolean,
    required: false,
    default: false,
  },
})

// Models
const searchModel = defineModel('search', { type: String, default: '' })
const filterToggleModel = defineModel('filterToggle', {
  type: Object,
  required: false,
  default: null,
})

const toggleModel = defineModel('toggle', { type: Boolean, default: false, required: false })

const debouncedModel = ref(searchModel.value)

// Refs
const menuVisible = ref(false)
const filterMenu = ref(null)
const filterModel = ref(cloneDeep(toRaw(filterToggleModel.value)))
const selectBoxes = ref({})

// Computed properties
const translationString = computed(() => {
  let prefix = process.env.FRONTEND_TYPE + '.' + capitalize(props.itemsService) + '.'
  if (props.itemsService === 'users') {
    prefix = 'Shared.Users.'
  }
  return prefix
})

const computedHeading = computed(() => {
  return (
    '<' +
    props.headingTag +
    ' class="omni-top-header ellipsis' +
    (props.removeTopPadding ? ' remove-top-padding' : '') +
    '">' +
    props.heading +
    '</' +
    props.headingTag +
    '>'
  )
})

const showToolbar = computed(() => {
  return (
    props.searchable ||
    props.refreshItemList ||
    props.addItem ||
    props.additionalButtons.length > 0 ||
    (props.filters && props.filters.menuItems?.length > 0) ||
    Boolean(slots['top-slot-custom'])
  )
})
const actionButtonClass = computed(() => {
  return props.largeActionButtons ? 'button-height' : ''
})
const toolbarClass = computed(() => {
  return props.largeActionButtons ? 'items-start-forced q-py-sm' : 'items-center'
})

// Methods
/**
 * On a filter select change, emit it to the parent
 */
const emitValue = () => {
  if (!isEqual(filterModel.value, filterToggleModel.value)) {
    filterToggleModel.value = cloneDeep(toRaw(filterModel.value))
  }
}

/**
 * Count the length of the data
 * @param options
 * @returns {*}
 */
const optionsCount = (options) => {
  return createOptions(options).filter((option) => !option.disable).length
}
/**
 * Create a usable options set
 * @param item
 * @returns {[]|*[]}
 */
const createOptions = (item) => {
  return getOptions(item.key, item.value, props.itemsService, undefined, undefined, props.translateFilterOptions)
}

// Watchers
watch(
  filterToggleModel,
  (value) => {
    filterModel.value = cloneDeep(toRaw(value))
  },
  { deep: true },
)
</script>

<template>
  <div class="row full-width items-end">
    <div v-if="props.heading" v-html="computedHeading"></div>
    <q-toolbar :class="['q-px-none', toolbarClass]" v-if="showToolbar">
      <q-btn
        v-if="refreshItemList"
        :class="[actionButtonClass, $q.screen.xs ? 'q-mb-sm' : '']"
        icon-right="sync"
        color="primary"
        @click="refreshItemList"
        :aria-label="$t('Shared.Globals.Refresh list')"
      >
        <q-tooltip :delay="500">
          {{ $t('Shared.Globals.Refresh list') }}
        </q-tooltip>
      </q-btn>
      <q-btn
        v-if="addItem"
        :class="[actionButtonClass, 'q-ml-sm', $q.screen.xs ? 'q-mb-sm' : '']"
        icon="add"
        color="primary"
        :label="$q.screen.gt.sm ? (addItemLabel ? addItemLabel : $t(translationString + 'New item')) : ''"
        :aria-label="addItemLabel || $t(translationString + 'New item')"
        @click.prevent="addItem('new', {})"
      >
        <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
          {{ addItemLabel || $t(translationString + 'New item', 2) }}
        </q-tooltip>
      </q-btn>
      <q-btn
        v-if="filters && filters.menuItems?.length > 0"
        :class="[actionButtonClass, 'q-ml-sm', $q.screen.xs ? 'q-mb-sm' : '']"
        color="primary"
        :label="$q.screen.gt.sm ? $t('Shared.Globals.Filters') : ''"
        :aria-label="$t('Shared.Globals.Filters')"
        icon="filter_alt"
      >
        <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
          {{ $t('Shared.Globals.Filters', 2) }}
        </q-tooltip>
        <q-menu ref="filterMenu" @hide="emitValue">
          <q-list :dense="!$q.platform.is.mobile" :class="$q.platform.is.mobile || $q.screen.xs ? 'full-width' : 'width-350'">
            <template v-for="(item, itemIndex) in filters.menuItems" :key="itemIndex">
              <q-expansion-item
                :class="{ 'full-width': $q.platform.is.mobile }"
                expand-separator
                :default-opened="$q.platform.is.mobile"
                :label="$t(translationString + capitalize(item.key))"
              >
                <q-separator color="primary" />
                <q-card>
                  <slot :name="'filter-' + item.key" :model="filterModel" :item="item">
                    <q-card-section class="q-py-none">
                      <div class="row">
                        <div class="col-12">
                          <omni-select
                            :ref="(el) => (selectBoxes[itemIndex] = el)"
                            :outlined="false"
                            :searchable-select="typeof item.multiple !== 'undefined' ? item.multiple : true"
                            :dense="!$q.platform.is.mobile"
                            v-model="filterModel[item.key]"
                            :clearable="typeof item.multiple !== 'undefined' ? item.multiple : true"
                            :multiple="typeof item.multiple !== 'undefined' ? item.multiple : true"
                            emit-value
                            map-options
                            :options="createOptions(item)"
                          >
                            <template #selected="{}" v-if="typeof item.multiple === 'undefined' || item.multiple === true">
                              <div class="ellipsis">
                                {{
                                  !selectBoxes[itemIndex]?.isFocused
                                    ? $t('Shared.Globals.Selected items', {
                                        items: 'Shared.Globals.Options',
                                        selected: filterModel[item.key]?.length || 0,
                                        total: optionsCount(item),
                                      })
                                    : ''
                                }}
                              </div>
                            </template>
                            <template #option="{ itemProps, opt, selected, toggleOption }">
                              <q-item v-bind="itemProps" dense>
                                <q-item-section v-if="opt.avatar" avatar>
                                  <q-icon class="q-pa-md small-text" :class="opt.avatar"></q-icon>
                                </q-item-section>
                                <q-item-section side v-if="(typeof item.multiple === 'undefined' || item.multiple === true) && !opt.disable">
                                  <q-checkbox :model-value="selected" @update:model-value="toggleOption(opt)" />
                                </q-item-section>
                                <q-item-section>
                                  <q-item-label v-if="opt.caption" :class="opt.class || undefined"
                                    ><strong>{{ opt.caption }}</strong></q-item-label
                                  >
                                  <q-item-label :class="{ 'text-grey small-text line-breaks': opt.caption }"
                                    >{{ opt.prefix ? opt.prefix + ' - ' : '' }}{{ opt.label ? opt.label : opt.value[optionsConfig?.labelKey] }}
                                  </q-item-label>
                                  <q-tooltip v-if="opt.tooltip" :delay="500" class="line-breaks">{{ opt.tooltip }} </q-tooltip>
                                </q-item-section>
                              </q-item>
                            </template>
                          </omni-select>
                        </div>
                      </div>
                    </q-card-section>
                  </slot>
                </q-card>
              </q-expansion-item>
            </template>
          </q-list>
        </q-menu>
      </q-btn>
      <template v-if="additionalButtons.length > 0">
        <template v-for="(button, buttonIndex) in additionalButtons" :key="buttonIndex">
          <q-separator vertical v-if="button.separator" class="q-ml-md q-mr-sm" />

          <q-toggle v-if="button.toggle" v-model="toggleModel" color="primary" :label="$q.screen.gt.sm ? button.label : undefined">
            <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
              {{ button.tooltip ? button.tooltip : button.label }}
            </q-tooltip>
          </q-toggle>

          <q-btn
            v-else-if="!button.actionGroup"
            :class="{ 'q-mb-sm': $q.screen.xs, 'q-ml-sm': (buttonIndex === 0 && (refreshItemList || addItem || filters)) || buttonIndex > 0 }"
            :icon-right="button.iconSide === 'right' ? button.icon : undefined"
            :icon="button.iconSide === 'left' ? button.icon : undefined"
            :color="button.color ? button.color : 'primary'"
            @click="button.action()"
            :aria-label="button.label"
            :label="$q.screen.gt.sm ? button.label : undefined"
            :disable="button.disabled"
            :loading="button.loading"
            :flat="button.flat"
            :round="button.round"
            :fab="button.fab"
            :fab-mini="button.fabMini"
          >
            <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
              {{ button.tooltip ? button.tooltip : button.label }}
            </q-tooltip>
          </q-btn>

          <q-btn
            v-else-if="button.actionGroup"
            class="q-ml-sm"
            color="primary"
            :label="$q.screen.gt.md ? button.label : ''"
            no-caps
            :icon="button.icon"
            :icon-right="$q.screen.lt.md ? undefined : 'arrow_drop_down'"
            :class="{ 'menu-opened': menuVisible }"
          >
            <q-tooltip v-if="$q.screen.lt.md && button.tooltip" :delay="500">
              {{ button.tooltip }}
            </q-tooltip>
            <q-menu fit anchor="bottom left" v-model="menuVisible">
              <q-list>
                <q-item
                  v-for="(groupButton, groupButtonIndex) in button.buttonGroup"
                  :key="buttonIndex + '-' + groupButtonIndex"
                  clickable
                  v-close-popup
                  @click="groupButton.action"
                >
                  <q-item-section>
                    <q-item-label class="flex items-center">
                      <q-icon v-if="groupButton.icon" size="1.5rem" :name="groupButton.icon" :color="groupButton.iconColor" left />
                      {{ groupButton.label }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </template>
      </template>

      <slot name="top-slot-custom"></slot>
      <slot name="toolbar-middle"></slot>
      <q-toolbar-title></q-toolbar-title>
      <q-input
        v-if="searchable"
        :lang="useUserStore().regionalSettings"
        dense
        hide-bottom-space
        debounce="1000"
        color="primary"
        v-model="searchModel"
        class="no-error q-field--error"
        data-lpignore="true"
        @blur="searchModel = $event.target.value"
        @keyup.enter="searchModel = $event.target.value"
      >
        <template #append>
          <q-icon name="search" />
        </template>
      </q-input>
    </q-toolbar>
  </div>
</template>

<style scoped></style>
